import { FC, Fragment, useCallback } from 'react'
import styled from 'styled-components'
import {
  BottomDocked,
  BreakLongWords,
  Button,
  Caption,
  Col,
  colors,
  Content,
  // H1,
  H2,
  // H3,
  H4,
  Page,
  Row,
  SmallButton,
  // Text,
  Spacer,
  T,
} from '../components'
import { getDayNameLong, getTimeString } from '../helper/DateFornat'
import { useBackButton } from '../hooks/useBackButton'
import { closeFilter } from '../state/filter/filterSlice'
import { useAppDispatch, useAppSelector } from '../state/hooks'
import { useLang } from '../state/language'
import { toggleFav } from '../state/timetable/timetableSlice'
import { PlusButton } from '../TimetableList/PlusButton'
import { getArtistWithShows } from '../TimetableList/selectors'
import { ArtistNotesAndHighlight } from './ArtistNotesAndHighlight'
import { ArtistTextSection } from './ArtistTextSection'
import { ShowMetaItem } from './ShowMetaItem'

const Buttons = styled.div`
  > * {
    margin-right: 1rem;
    margin-bottom: 1rem;
  }
`

const SShow = styled.div`
  margin-bottom: 1.5rem;
`

const PlayTimes = styled.div`
  background: ${colors.SpecialContent.bg};
  border-radius: 1rem;
  padding: 1.5rem;
`

const Divider = styled.div`
  border-bottom: 1px solid ${colors.SpecialContent.divider};
`

const Chip = styled(Caption)`
  background-color: ${colors.Chip.bg};
  border-radius: 1rem;
  padding: 0.5rem 1rem;
  color: ${colors.textAlt};
`

type Props = {
  id: string
}

const ArtistInfo: FC<Props> = ({ id }) => {
  const dispatch = useAppDispatch()

  const artist = useAppSelector((state) => getArtistWithShows(state, id))
  const lang = useLang()

  const close = useCallback(() => {
    dispatch(closeFilter())
  }, [dispatch])

  useBackButton({ onBackClick: close, enabled: true })

  if (!artist) {
    return null
  }

  const {
    artist: artistName,
    descDe,
    descEn,
    website,
    soundsample,
    shows,
    genre,
  } = artist

  console.log('artist: ', artist)

  let text = lang === 'de' ? descDe : descEn

  // use other language text of own lang is not there
  if (!text) {
    text = lang === 'de' ? descEn : descDe
  }

  return (
    <Page withBackground={true} alignLeft center>
      <Content color={colors.textAlt}>
        {artist.name && (
          <>
            <H4 $mb={4}>
              <BreakLongWords>{artist.name}</BreakLongWords>
            </H4>
          </>
        )}
        <H2 $mb={4}>
          <BreakLongWords>{artistName}</BreakLongWords>
        </H2>

        {genre && (
          <Col gap={3} flexDirection="row" flexWrap="wrap" mt={5} mb={5}>
            <Chip>{genre}</Chip>
            {artist.category && <Chip>{artist.category}</Chip>}
            {artist.musicGenre.genre1 && (
              <Chip>{artist.musicGenre.genre1}</Chip>
            )}
            {artist.musicGenre.genre2 && (
              <Chip>{artist.musicGenre.genre2}</Chip>
            )}
          </Col>
        )}

        {text && (
          <ArtistTextSection
            // title={<T id="ARTIST_INFO_INFO" />}
            content={text}
          />
        )}

        {artist.fromAndWith && (
          <ArtistTextSection
            title={<T id="ARTIST_INFO_FROM_AND_WITH" />}
            content={artist.fromAndWith}
          />
        )}

        {artist.showDuration && (
          <ArtistTextSection
            title={<T id="ARTIST_INFO_DURATION" />}
            content={artist.showDuration}
          />
        )}

        {artist.language && (
          <ArtistTextSection
            title={<T id="ARTIST_INFO_LANGUAGE" />}
            content={artist.language}
          />
        )}

        {artist.minimumAge && (
          <ArtistTextSection
            title={<T id="ARTIST_INFO_MIN_AGE" />}
            content={artist.minimumAge}
          />
        )}

        {(!!website || !!soundsample) && (
          <>
            <Spacer size={1} />
            <Buttons>
              {!!website && (
                <SmallButton
                  onClick={() => window.open(website, '_blank')}
                  mt={3}
                >
                  Homepage
                </SmallButton>
              )}

              {!!soundsample && (
                <SmallButton onClick={() => window.open(soundsample, '_blank')}>
                  Sound sample
                </SmallButton>
              )}
            </Buttons>
          </>
        )}

        {!!shows && shows.length > 0 && (
          <>
            <Spacer size={2} />
            <PlayTimes>
              <H4>
                <T id="ARTIST_INFO_PLAYTIME" />
              </H4>
              <Spacer size={1} />
              <Divider />
              <Spacer size={1} />
              {shows.map((show) => {
                const start = new Date(show.start)
                const end = new Date(show.end)
                return (
                  <Fragment key={show.showId}>
                    <SShow>
                      <Row alignItems="flex-start">
                        <Col flex={1} gap={1} flexDirection="column">
                          <ShowMetaItem icon="day">
                            {getDayNameLong(start)}
                          </ShowMetaItem>

                          <ShowMetaItem icon="time">
                            {getTimeString(start)} - {getTimeString(end)}
                          </ShowMetaItem>

                          <ShowMetaItem icon="floor">{show.floor}</ShowMetaItem>
                        </Col>

                        <PlusButton
                          $isFavorite={show.isFavorite}
                          $toggleColor
                          onClick={() => {
                            dispatch(toggleFav(show.showId))
                          }}
                        />
                      </Row>
                    </SShow>
                    <Spacer size={1} />
                    <Divider />
                    <Spacer size={1} />
                  </Fragment>
                )
              })}
            </PlayTimes>
          </>
        )}

        <Spacer size={1} />
        <ArtistNotesAndHighlight artist={artist} />
      </Content>
      <BottomDocked>
        <Row>
          <Button onClick={close}>
            <T id="INFO_CLOSE_BTN" />
          </Button>
        </Row>
      </BottomDocked>
    </Page>
  )
}

export default ArtistInfo
